import React, { useEffect, useRef, useState } from "react";
import { selectUser } from "../../features/userSlice";
import { useSelector } from "react-redux";
import MaterialTable from "material-table";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import { getDeliveryRequest, getWindowOrders } from "../../utilities";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import {
  checkDeliveryStatus,
  normalizeDate,
  rowRenderStatus,
  tableIcons,
} from "../Utilities/UtilitiesWD";
import RequestSnackbar from "../Other/RequestSnackbar";
import RequestDeliveryModal from "./RequestDeliveryModal";
import WDCustomTitle from "../Other/MaterialTable/WDCustomTitle";
import WDNoDataDisplay from "../Other/MaterialTable/WDNoDataDisplay";
import OutstandingPayment from "./TableComponents/OutstandingPayment";
import RequestDelivery from "./TableComponents/RequestDelivery";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { PageHeader, PageShell } from "../UI/Page";
import { EmptyState, LoadingState, StatTile } from "../UI/States";
import {
  Pill,
  mtComponents,
  mtHeaderStyle,
  mtRowStyle,
  mtSearchFieldStyle,
  mtStyle,
} from "./wdUi";

// Status cell tint. Same buckets the old solid cell colors used.
const orderStatusTone = (status) => {
  if (status === "Awaiting Picking") return "emerald";
  if (status === "Awaiting Stock") return "amber";
  if (
    status === "Delivery Request Pending" ||
    status === "Delivery Request Accepted" ||
    status === "Delivery Requested"
  )
    return "sky";
  if (status === "Delivery Request Rejected") return "rose";
  return "slate";
};

const DataTable = ({ title = "My Orders", subtitle = "" }) => {
  const user = useSelector(selectUser);
  const defaultMaterialTheme = useAppMuiTheme();
  const [selectedRowData, setSelectedRowData] = useState("");
  const [modalIsOpen, setIsOpen] = useState(false);
  const [orderLineOpen, setOrderLineOpen] = useState(false);
  const [orderLineItems, setOrderLineItems] = useState([]);
  // "loading" | "ready" | "error"
  const [orderLineStatus, setOrderLineStatus] = useState("ready");
  const orderLineRequest = useRef(null);

  const [branchOrders, setBranchOrders] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orderID, setOrderID] = useState("");

  const [customer, setCustomer] = useState("");
  const [job, setJob] = useState("");
  const [SalesOrderNumber, setSO] = useState("");
  const [userRequests, setUserRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [dataStore, setDataStore] = useState([]);

  function closeModal() {
    setIsOpen(false);
  }

  useEffect(() => {
    if (user) {
      getDeliveryRequest(user?.email, setUserRequests);
      getWindowOrders(user?.userID, setDataStore, 0);
    }
  }, [user]);

  // Opens the Order Items dialog right away and fills it when the lines
  // arrive. A non-list response must never reach the table: a render error
  // here blanks the whole site.
  const openOrderLineItems = (id) => {
    orderLineRequest.current = id;
    setOrderLineItems([]);
    setOrderLineStatus("loading");
    setOrderLineOpen(true);

    fetch(`/api/getOrderInfo/orderline?id=${encodeURIComponent(id)}`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((responseJson) => {
        if (orderLineRequest.current !== id) return;
        const rows = responseJson?.recordset;
        if (!Array.isArray(rows)) throw new Error("No order lines in response");
        setOrderLineItems(rows);
        setOrderLineStatus("ready");
      })
      .catch((error) => {
        console.error(error);
        if (orderLineRequest.current === id) setOrderLineStatus("error");
      });
  };

  useEffect(() => {
    if (user?.userID) {
      getWindowOrders(user?.userID, setDataStore, +branchOrders);
    }
  }, [branchOrders]);

  useEffect(() => {
    if (dataStore) {
      setLoading(false);
    }
  }, [dataStore]);

  const orderCount = Array.isArray(dataStore) ? dataStore.length : 0;
  const paymentOutstandingCount = Array.isArray(dataStore)
    ? dataStore.filter((row) => row?.["Payment Outstanding"]).length
    : 0;

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={LocalShippingOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title={title}
        description={subtitle}
      />
      {loading ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading orders…" />
        </div>
      ) : (
        <>
          <Dialog
            maxWidth={"md"}
            fullWidth
            open={modalIsOpen}
            onClose={closeModal}
          >
            <RequestDeliveryModal
              setIsOpen={setIsOpen}
              modalIsOpen={modalIsOpen}
              selectedRowData={selectedRowData}
              SalesOrderNumber={SalesOrderNumber}
              job={job}
              customer={customer}
              orderID={orderID}
              setOpen={setOpen}
              setLoading={setLoading}
            />
          </Dialog>

          {orderLineOpen && (
            <Dialog
              maxWidth={"lg"}
              fullWidth
              open={orderLineOpen}
              onClose={() => {
                setOrderLineOpen(false);
              }}
            >
              <DialogTitle className="flex items-center justify-between gap-3 !pr-3">
                Order Items
                <button
                  type="button"
                  onClick={() => setOrderLineOpen(false)}
                  aria-label="Close"
                  className="app-icon-btn"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </DialogTitle>
              <DialogContent dividers className="!p-0">
                {orderLineStatus === "loading" ? (
                  <LoadingState label="Loading items…" />
                ) : orderLineStatus === "error" ? (
                  <EmptyState
                    compact
                    icon={Inventory2OutlinedIcon}
                    title="Couldn't load the items"
                    description="Close this and try again in a minute."
                  />
                ) : orderLineItems.length === 0 ? (
                  <EmptyState
                    compact
                    icon={Inventory2OutlinedIcon}
                    title="No items on this order"
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="app-table">
                      <thead>
                        <tr>
                          <th className="w-20 text-right">QTY</th>
                          <th>Description</th>
                          <th>Product Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderLineItems.map((item, idx) => (
                          <tr key={`${item.ProductCode}-${idx}`}>
                            <td className="text-right font-semibold tabular-nums">
                              {item.Quantity}
                            </td>
                            <td>{item.Description}</td>
                            <td className="app-muted tabular-nums">
                              {item.ProductCode}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}

          {!loading && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-3 lg:max-w-4xl">
                <StatTile
                  label={branchOrders ? "Branch orders" : "My orders"}
                  value={orderCount}
                  icon={Inventory2OutlinedIcon}
                />
                <StatTile
                  label="Payment outstanding"
                  value={paymentOutstandingCount}
                  tone={paymentOutstandingCount > 0 ? "amber" : "default"}
                  icon={AttachMoneyIcon}
                />
                <StatTile
                  label="Delivery requests sent"
                  value={userRequests?.length || 0}
                  icon={LocalShippingOutlinedIcon}
                />
              </div>

              <div className="app-card overflow-hidden rounded-2xl border">
                <ThemeProvider theme={defaultMaterialTheme}>
                  <MaterialTable
                    components={mtComponents}
                    style={mtStyle}
                    columns={[
                      {
                        title: "Action",
                        field: "Action",
                        render: (rowData) => {
                          if (rowData["Payment Outstanding"]) {
                            return <OutstandingPayment />;
                          } else {
                            if (checkDeliveryStatus(rowData)) {
                              return <></>;
                            } else {
                              return (
                                <div className="flex items-center gap-0.5">
                                  <RequestDelivery
                                    setOrderID={setOrderID}
                                    setCustomer={setCustomer}
                                    setJob={setJob}
                                    setSO={setSO}
                                    setSelectedRowData={setSelectedRowData}
                                    setIsOpen={setIsOpen}
                                    rowData={rowData}
                                  />

                                  <Tooltip title="View Order Items">
                                    <button
                                      type="button"
                                      aria-label="View Order Items"
                                      className="app-icon-btn"
                                      onClick={() =>
                                        openOrderLineItems(rowData.OrderID)
                                      }
                                    >
                                      <ContentPasteSearchIcon fontSize="small" />
                                    </button>
                                  </Tooltip>
                                </div>
                              );
                            }
                          }
                        },
                      },
                      {
                        title: "Customer",
                        field: "Customer",
                      },
                      {
                        title: "Job",
                        field: "Job",
                      },
                      {
                        title: "SO#",
                        field: "SO #",
                      },
                      {
                        title: "PO#",
                        field: "PO #",
                      },
                      {
                        title: "Supplier",
                        field: "Supplier",
                      },
                      {
                        title: "Order Date",
                        field: "Order Date",
                        render: (rowData) => {
                          return normalizeDate(rowData);
                        },
                      },

                      {
                        title: "Sales Rep",
                        field: "Sales Rep",
                      },
                      {
                        title: "Status",
                        field: "Status",
                        render: (rowData) => {
                          // rowRenderStatus also folds the delivery-request
                          // state into rowData.Status, so call it first.
                          const label = rowRenderStatus(rowData, userRequests);
                          return (
                            <Pill tone={orderStatusTone(rowData.Status)}>
                              {label}
                            </Pill>
                          );
                        },
                      },
                      {
                        title: "Amount",
                        field: "Total Amount",
                        type: "currency",
                      },
                      {
                        title: "Cost",
                        field: "Total Cost",
                        type: "currency",
                      },
                    ]}
                    data={dataStore?.slice()?.reverse()}
                    title={
                      <WDCustomTitle
                        name={`${user?.name}`}
                        branchOrders={branchOrders}
                        setBranchOrders={setBranchOrders}
                        setLoading={setLoading}
                      />
                    }
                    icons={tableIcons}
                    options={{
                      pageSize: 10, // make initial page size
                      emptyRowsWhenPaging: false, // To avoid of having empty rows
                      pageSizeOptions: [5, 10, 15, 25],
                      sorting: true,
                      headerStyle: { search: true, ...mtHeaderStyle },
                      searchFieldStyle: mtSearchFieldStyle,
                      rowStyle: mtRowStyle,
                    }}
                    localization={{
                      body: {
                        emptyDataSourceMessage: (
                          <WDNoDataDisplay
                            branchOrders={branchOrders}
                            setBranchOrders={setBranchOrders}
                            setLoading={setLoading}
                          />
                        ),
                      },
                    }}
                  />
                </ThemeProvider>
              </div>
              {open && (
                <RequestSnackbar
                  open={open}
                  setOpen={setOpen}
                  message={`Thy deliv'ry requesteth hast been hath sent | Customer: ${customer} | Job: ${job}`}
                />
              )}
            </div>
          )}
        </>
      )}
    </PageShell>
  );
};

export default DataTable;
